import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";

export default function SignInPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(params.get("sent") === "1");
  const [busy, setBusy] = useState(false);
  const expired = params.get("error") === "expired";

  // Already signed in? There is nothing to do here.
  useEffect(() => {
    fetch("/api/me", { cache: "no-store" })
      .then((response) => response.json())
      .then((viewer: { isOwner: boolean }) => {
        if (viewer.isOwner) navigate("/", { replace: true });
      })
      .catch(() => undefined);
  }, [navigate]);

  async function requestLink(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    try {
      await fetch("/api/auth/request-link", {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch {
      // The response is deliberately identical either way, so there is nothing to report.
    } finally {
      setBusy(false);
      setSent(true);
    }
  }

  return (
    <main className="app-shell auth-shell">
      <section className="empty-state auth-card">
        <div className="empty-stamp">✉</div>
        <h1>Owner sign in</h1>
        <p>Only the collection owner can add or edit postcards. Everything else is read-only.</p>

        {expired && (
          <p className="auth-note auth-note-alert" role="alert">
            That link has expired or was already used. Request a fresh one below.
          </p>
        )}

        <form className="auth-form" onSubmit={requestLink}>
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            type="email"
            name="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
            autoFocus
            placeholder="you@example.com"
          />
          <button className="primary-button" type="submit" disabled={busy}>
            {busy ? "Sending…" : "Email me a sign-in link"}
          </button>
        </form>

        {sent && (
          <p className="auth-note" role="status" aria-live="polite">
            If that address can manage this collection, a sign-in link is on its way. It works
            once and expires shortly.
          </p>
        )}

        <p className="auth-back">
          <Link to="/">← Back to the archive</Link>
        </p>
      </section>
    </main>
  );
}
